import React, { useEffect, useMemo, useState } from "react";
import TopBar from "../components/TopBar";
import EmptyState from "../components/EmptyState";
import { api, fmtUSD, fmtNum, todayISO } from "../lib/api";
import AddableSelect from "../components/AddableSelect";
import { Modal, Field } from "./Fields";
import { Plus, Pencil, Trash2, Store, ExternalLink, RefreshCw, MapPin, TrendingUp, TrendingDown, ClipboardPaste, Sparkles, Check, X } from "lucide-react";
import { toast } from "sonner";

const KINDS = [
  { value: "sale_barn",      label: "Sale Barn" },
  { value: "grain_terminal", label: "Grain Terminal" },
  { value: "cash_bid",       label: "Cash Bidder" },
  { value: "other",          label: "Other" },
];

const UNITS = ["$/bu", "$/cwt", "$/head", "$/lb", "$/ton"];

const DEFAULT_COMMODITIES = [
  "Corn", "Soybeans", "Wheat", "Oats", "Milo",
  "Feeder Steer 400-500#", "Feeder Steer 500-600#", "Feeder Steer 600-700#",
  "Feeder Heifer 400-500#", "Feeder Heifer 500-600#",
  "Cull Cow", "Slaughter Bull", "Bred Cow", "Pair",
  "Slaughter Hog", "Feeder Pig", "Lamb", "Cull Ewe",
];

const blankLoc = { name: "", city: "", state: "", kind: "sale_barn", phone: "", website: "", fetch_url: "", notes: "" };
const blankPrice = { location_id: "", commodity: "Corn", date: todayISO(), price: "", unit: "$/bu", weight_class: "", grade: "", notes: "" };

export default function Markets() {
  const [locations, setLocations] = useState([]);
  const [prices, setPrices] = useState([]);
  const [compare, setCompare] = useState(null);
  const [commodity, setCommodity] = useState("Corn");
  const [locOpen, setLocOpen] = useState(false);
  const [locEditing, setLocEditing] = useState(null);
  const [locForm, setLocForm] = useState(blankLoc);
  const [priceOpen, setPriceOpen] = useState(false);
  const [priceForm, setPriceForm] = useState(blankPrice);

  // Paste-in / LLM extract
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteState, setPasteState] = useState({ location_id: "", date: todayISO(), text: "", extracting: false, rows: [], selected: {} });

  const loadCore = async () => {
    const [l, p] = await Promise.all([api.get("/market-locations"), api.get("/market-prices")]);
    setLocations(l.data); setPrices(p.data);
  };
  const loadCompare = async (c) => {
    const { data } = await api.get(`/market-compare?commodity=${encodeURIComponent(c)}`);
    setCompare(data);
  };
  useEffect(() => { loadCore(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadCompare(commodity); /* eslint-disable-next-line */ }, [commodity, prices.length]);

  const commodityChoices = useMemo(() => {
    const s = new Set(DEFAULT_COMMODITIES);
    prices.forEach((p) => s.add(p.commodity));
    return Array.from(s).sort();
  }, [prices]);

  const onLocSubmit = async (e) => {
    e.preventDefault();
    try {
      if (locEditing) await api.put(`/market-locations/${locEditing.id}`, locForm);
      else            await api.post(`/market-locations`, locForm);
      toast.success(locEditing ? "Location updated" : "Location added");
      setLocOpen(false); setLocEditing(null); setLocForm(blankLoc); loadCore();
    } catch { toast.error("Could not save location"); }
  };
  const editLoc = (l) => { setLocEditing(l); setLocForm({ ...blankLoc, ...l }); setLocOpen(true); };
  const deleteLoc = async (id) => {
    if (!window.confirm("Delete this market location and all its prices?")) return;
    await api.delete(`/market-locations/${id}`);
    toast.success("Location deleted");
    loadCore();
  };

  const openPrice = (locId) => {
    setPriceForm({ ...blankPrice, location_id: locId || "", commodity, date: todayISO() });
    setPriceOpen(true);
  };
  const onPriceSubmit = async (e) => {
    e.preventDefault();
    if (!priceForm.location_id) return toast.error("Pick a location");
    try {
      await api.post(`/market-prices`, {
        ...priceForm,
        price: parseFloat(priceForm.price) || 0,
      });
      toast.success("Price saved");
      setPriceOpen(false); setPriceForm(blankPrice); loadCore();
    } catch { toast.error("Could not save price"); }
  };
  const deletePrice = async (id) => {
    await api.delete(`/market-prices/${id}`);
    loadCore();
  };

  const openPaste = (locId) => {
    setPasteState({ location_id: locId || "", date: todayISO(), text: "", extracting: false, rows: [], selected: {} });
    setPasteOpen(true);
  };
  const runExtract = async () => {
    if (!pasteState.text.trim()) return toast.error("Paste the report text first");
    setPasteState((s) => ({ ...s, extracting: true }));
    try {
      const { data } = await api.post(`/market-prices/extract`, { text: pasteState.text, location_id: pasteState.location_id });
      const selected = {};
      data.rows.forEach((_, i) => { selected[i] = true; });  // default: all selected
      setPasteState((s) => ({ ...s, rows: data.rows, selected, extracting: false }));
      toast.success(`Gemini pulled ${data.count} row${data.count === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extraction failed");
      setPasteState((s) => ({ ...s, extracting: false }));
    }
  };
  const saveSelected = async () => {
    if (!pasteState.location_id) return toast.error("Pick a location first");
    const picks = pasteState.rows.filter((_, i) => pasteState.selected[i]);
    if (!picks.length) return toast.error("Nothing selected");
    try {
      await Promise.all(picks.map((r) => api.post(`/market-prices`, {
        location_id: pasteState.location_id,
        commodity: r.commodity,
        date: pasteState.date,
        price: r.price,
        unit: r.unit,
        weight_class: r.weight_class || "",
        grade: r.grade || "",
      })));
      toast.success(`Saved ${picks.length} quote${picks.length === 1 ? "" : "s"}`);
      setPasteOpen(false); loadCore();
    } catch { toast.error("Could not save all rows"); }
  };
  const editRow = (idx, patch) => {
    setPasteState((s) => ({ ...s, rows: s.rows.map((r, i) => i === idx ? { ...r, ...patch } : r) }));
  };
  const toggleRow = (idx) => {
    setPasteState((s) => ({ ...s, selected: { ...s.selected, [idx]: !s.selected[idx] } }));
  };

  const rows = compare?.rows || [];

  return (
    <div>
      <TopBar
        title="Market Watch"
        subtitle="Track prices at sale barns + grain terminals. Compare side-by-side. Nothing runs in the background — hit Check Prices when you're ready."
        actions={
          <div className="flex gap-2 mt-6 flex-wrap">
            <button data-testid="add-location-btn" onClick={() => { setLocEditing(null); setLocForm(blankLoc); setLocOpen(true); }} className="bg-[#BC4727] text-white px-4 py-2 font-bold uppercase tracking-wider text-sm hover:bg-[#9D391E] flex items-center gap-2">
              <Plus className="w-4 h-4" /> Location
            </button>
            <button data-testid="add-price-btn" onClick={() => openPrice("")} className="bg-[#1A4135] text-white px-4 py-2 font-bold uppercase tracking-wider text-sm hover:bg-[#133128] flex items-center gap-2">
              <Plus className="w-4 h-4" /> Price
            </button>
            <button data-testid="paste-report-btn" onClick={() => openPaste("")} className="bg-[#5B2C82] text-white px-4 py-2 font-bold uppercase tracking-wider text-sm hover:bg-[#4A2469] flex items-center gap-2">
              <ClipboardPaste className="w-4 h-4" /> Paste Report
            </button>
          </div>
        }
      />

      {/* Comparison Widget */}
      <div className="bg-white border-2 border-[#1F1E1B] mb-6" data-testid="market-compare">
        <div className="flex flex-wrap items-baseline gap-3 border-b border-[#1F1E1B] px-4 py-3 bg-[#F5F4F0]">
          <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Compare</label>
          <select data-testid="commodity-select" value={commodity} onChange={(e) => setCommodity(e.target.value)} className="bg-white border border-[#1F1E1B] px-3 py-1 text-sm font-bold tabular">
            {commodityChoices.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {compare && rows.length > 0 && (
            <div className="ml-auto flex flex-wrap gap-4 text-xs">
              <span><span className="text-[#66645E] uppercase tracking-wider text-[10px]">Top</span> <strong className="tabular text-[#1A4135]">{fmtUSD(compare.top_price)}</strong> · {compare.top_location}</span>
              <span><span className="text-[#66645E] uppercase tracking-wider text-[10px]">Spread</span> <strong className="tabular text-[#BC4727]">{fmtUSD(compare.spread)}</strong></span>
              <span><span className="text-[#66645E] uppercase tracking-wider text-[10px]">Locations</span> <strong className="tabular">{compare.location_count}</strong></span>
            </div>
          )}
        </div>

        {rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#66645E]" data-testid="market-compare-empty">
            No prices yet for <strong>{commodity}</strong>. Add a location (like <em>Roanoke, WV sale barn</em>), then hit <strong>+ Price</strong> or the row's <strong>Check Prices</strong> button.
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="market-compare-table">
            <table className="w-full text-sm">
              <thead className="bg-[#FAF8F2] border-b-2 border-[#1F1E1B]">
                <tr>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#66645E]">Rank</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#66645E]">Location</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#66645E]">Class / Grade</th>
                  <th className="num px-3 py-2 text-[10px] uppercase tracking-wider text-[#66645E]">Price</th>
                  <th className="num px-3 py-2 text-[10px] uppercase tracking-wider text-[#66645E]">vs Top</th>
                  <th className="num px-3 py-2 text-[10px] uppercase tracking-wider text-[#66645E]">As Of</th>
                  <th className="no-print"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.location_id} className="border-b border-[#E5E2D6] last:border-b-0" data-testid={`compare-row-${r.location_id}`}>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center justify-center w-6 h-6 text-[10px] font-bold text-white ${i === 0 ? "bg-[#1A4135]" : i === rows.length - 1 && rows.length > 1 ? "bg-[#BC4727]" : "bg-[#66645E]"}`}>
                        {i === 0 ? "★" : i + 1}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-semibold">{r.location_name}</div>
                      <div className="text-[11px] text-[#66645E] inline-flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {[r.city, r.state].filter(Boolean).join(", ") || "—"} · <span className="uppercase tracking-wider text-[9px]">{r.kind.replace("_", " ")}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs text-[#66645E]">{[r.weight_class, r.grade].filter(Boolean).join(" · ") || "—"}</td>
                    <td className="num px-3 py-2 tabular font-bold text-[#1F1E1B]">
                      {fmtUSD(r.price)} <span className="text-[10px] text-[#66645E] font-normal">{r.unit}</span>
                    </td>
                    <td className="num px-3 py-2 tabular">
                      {i === 0
                        ? <span className="text-[#1A4135] font-bold inline-flex items-center gap-0.5"><TrendingUp className="w-3 h-3" /> Best</span>
                        : <span className="text-[#BC4727] inline-flex items-center gap-0.5"><TrendingDown className="w-3 h-3" /> {fmtUSD(r.diff_vs_top)}</span>}
                    </td>
                    <td className="num px-3 py-2 text-[11px] text-[#66645E] tabular">{r.date}</td>
                    <td className="no-print px-3 py-2">
                      <div className="flex gap-1.5 justify-end">
                        {r.fetch_url && (
                          <a data-testid={`check-prices-${r.location_id}`} href={r.fetch_url} target="_blank" rel="noreferrer" title="Open today's price report" className="border border-[#1F1E1B] px-2 py-1 text-[10px] font-bold uppercase tracking-wider hover:bg-[#1F1E1B] hover:text-white inline-flex items-center gap-1">
                            <RefreshCw className="w-2.5 h-2.5" /> Check
                          </a>
                        )}
                        <button data-testid={`add-price-for-${r.location_id}`} onClick={() => openPrice(r.location_id)} className="border border-[#1A4135] text-[#1A4135] px-2 py-1 text-[10px] font-bold uppercase tracking-wider hover:bg-[#1A4135] hover:text-white">
                          + Price
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Locations list */}
      <h3 className="font-display text-xl font-black uppercase tracking-tight mb-3">Locations ({locations.length})</h3>
      {locations.length === 0 ? (
        <EmptyState icon={Store} title="No locations yet" hint="Add your first sale barn or grain terminal, then start recording prices." testid="empty-locations" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {locations.map((l) => (
            <div key={l.id} className="bg-white border border-[#1F1E1B] p-4" data-testid={`location-card-${l.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[9px] uppercase tracking-wider bg-[#F5F4F0] border border-[#D2CEC1] px-1.5 py-0.5 inline-block mb-1">{l.kind.replace("_", " ")}</div>
                  <h4 className="font-display text-lg font-bold truncate">{l.name}</h4>
                  <div className="text-xs text-[#66645E] inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{[l.city, l.state].filter(Boolean).join(", ") || "—"}</div>
                  {l.phone && <div className="text-xs text-[#66645E] mt-1">{l.phone}</div>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button data-testid={`edit-loc-${l.id}`} onClick={() => editLoc(l)} className="p-1.5 border border-[#1F1E1B] hover:bg-[#1F1E1B] hover:text-white"><Pencil className="w-3 h-3" /></button>
                  <button data-testid={`delete-loc-${l.id}`} onClick={() => deleteLoc(l.id)} className="p-1.5 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[#E5E2D6]">
                {l.fetch_url && (
                  <a href={l.fetch_url} target="_blank" rel="noreferrer" className="text-[10px] font-bold uppercase tracking-wider border border-[#1F1E1B] px-2 py-1 hover:bg-[#1F1E1B] hover:text-white inline-flex items-center gap-1">
                    <RefreshCw className="w-2.5 h-2.5" /> Check Prices
                  </a>
                )}
                {l.website && (
                  <a href={l.website} target="_blank" rel="noreferrer" className="text-[10px] uppercase tracking-wider text-[#66645E] px-2 py-1 hover:text-[#1F1E1B] inline-flex items-center gap-1">
                    <ExternalLink className="w-2.5 h-2.5" /> Website
                  </a>
                )}
                <button data-testid={`add-price-loc-${l.id}`} onClick={() => openPrice(l.id)} className="text-[10px] font-bold uppercase tracking-wider bg-[#1A4135] text-white px-2 py-1 hover:bg-[#133128]">
                  + Price
                </button>
                <button data-testid={`paste-report-loc-${l.id}`} onClick={() => openPaste(l.id)} className="text-[10px] font-bold uppercase tracking-wider bg-[#5B2C82] text-white px-2 py-1 hover:bg-[#4A2469] inline-flex items-center gap-1">
                  <ClipboardPaste className="w-2.5 h-2.5" /> Paste
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Location modal */}
      {locOpen && (
        <Modal title={locEditing ? "Edit Location" : "New Market Location"} onClose={() => { setLocOpen(false); setLocEditing(null); }}>
          <form onSubmit={onLocSubmit} className="space-y-3" data-testid="location-form">
            <Field label="Name" required>
              <input data-testid="loc-name-input" required value={locForm.name} onChange={(e) => setLocForm({ ...locForm, name: e.target.value })} placeholder="e.g. Roanoke Livestock Auction" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="City"><input data-testid="loc-city-input" value={locForm.city} onChange={(e) => setLocForm({ ...locForm, city: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
              <Field label="State"><input data-testid="loc-state-input" value={locForm.state} onChange={(e) => setLocForm({ ...locForm, state: e.target.value })} placeholder="WV" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm uppercase" /></Field>
              <Field label="Kind">
                <select data-testid="loc-kind-select" value={locForm.kind} onChange={(e) => setLocForm({ ...locForm, kind: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
                  {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Price-report URL">
              <input data-testid="loc-fetch-url-input" value={locForm.fetch_url} onChange={(e) => setLocForm({ ...locForm, fetch_url: e.target.value })} placeholder="https://…report.pdf or /prices" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              <div className="text-[10px] text-[#66645E] mt-1">The "Check Prices" button opens this link in a new tab so you can copy today's numbers.</div>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone"><input data-testid="loc-phone-input" value={locForm.phone} onChange={(e) => setLocForm({ ...locForm, phone: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
              <Field label="Website"><input data-testid="loc-website-input" value={locForm.website} onChange={(e) => setLocForm({ ...locForm, website: e.target.value })} placeholder="https://…" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
            </div>
            <Field label="Notes">
              <textarea data-testid="loc-notes-input" rows={2} value={locForm.notes} onChange={(e) => setLocForm({ ...locForm, notes: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => { setLocOpen(false); setLocEditing(null); }} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" data-testid="loc-submit-btn" className="bg-[#BC4727] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#9D391E]">Save Location</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Price modal */}
      {priceOpen && (
        <Modal title="New Price Quote" onClose={() => setPriceOpen(false)}>
          <form onSubmit={onPriceSubmit} className="space-y-3" data-testid="price-form">
            <Field label="Location" required>
              <select data-testid="price-loc-select" required value={priceForm.location_id} onChange={(e) => setPriceForm({ ...priceForm, location_id: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
                <option value="">Select location…</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name} · {[l.city, l.state].filter(Boolean).join(", ")}</option>)}
              </select>
            </Field>
            <Field label="Commodity" required>
              <AddableSelect
                domain="commodity"
                defaults={DEFAULT_COMMODITIES}
                value={priceForm.commodity}
                onChange={(v) => setPriceForm({ ...priceForm, commodity: v })}
                testid="price-commodity-select"
                required
              />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Date" required>
                <input data-testid="price-date-input" type="date" required value={priceForm.date} onChange={(e) => setPriceForm({ ...priceForm, date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              </Field>
              <Field label="Price" required>
                <input data-testid="price-value-input" type="number" step="0.0001" required value={priceForm.price} onChange={(e) => setPriceForm({ ...priceForm, price: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              </Field>
              <Field label="Unit">
                <select data-testid="price-unit-select" value={priceForm.unit} onChange={(e) => setPriceForm({ ...priceForm, unit: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
                  {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Weight class (cattle)">
                <input data-testid="price-wc-input" value={priceForm.weight_class} onChange={(e) => setPriceForm({ ...priceForm, weight_class: e.target.value })} placeholder="e.g. 500-600#" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
              <Field label="Grade">
                <input data-testid="price-grade-input" value={priceForm.grade} onChange={(e) => setPriceForm({ ...priceForm, grade: e.target.value })} placeholder="e.g. 1 Yellow, Choice" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
            </div>
            <Field label="Notes">
              <textarea data-testid="price-notes-input" rows={2} value={priceForm.notes} onChange={(e) => setPriceForm({ ...priceForm, notes: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setPriceOpen(false)} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" data-testid="price-submit-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128]">Save Price</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Paste-in / LLM extract modal */}
      {pasteOpen && (
        <Modal title="Paste Report — Gemini reads it" onClose={() => setPasteOpen(false)}>
          <div className="space-y-3" data-testid="paste-report-modal">
            <div className="text-xs text-[#66645E]">
              Copy today's price report from the barn/terminal's website, paste it below,
              and Gemini extracts every commodity + price. Review, deselect anything wrong, save.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Location" required>
                <select
                  data-testid="paste-loc-select"
                  required
                  value={pasteState.location_id}
                  onChange={(e) => setPasteState((s) => ({ ...s, location_id: e.target.value }))}
                  className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm"
                >
                  <option value="">Select location…</option>
                  {locations.map((l) => <option key={l.id} value={l.id}>{l.name} · {[l.city, l.state].filter(Boolean).join(", ")}</option>)}
                </select>
              </Field>
              <Field label="Report Date" required>
                <input
                  data-testid="paste-date-input"
                  type="date"
                  required
                  value={pasteState.date}
                  onChange={(e) => setPasteState((s) => ({ ...s, date: e.target.value }))}
                  className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular"
                />
              </Field>
            </div>
            <Field label="Paste report text">
              <textarea
                data-testid="paste-text-input"
                rows={8}
                value={pasteState.text}
                onChange={(e) => setPasteState((s) => ({ ...s, text: e.target.value }))}
                placeholder="Copy today's market report and paste here…"
                className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm font-mono"
              />
            </Field>
            <div className="flex justify-end">
              <button
                type="button"
                data-testid="paste-extract-btn"
                onClick={runExtract}
                disabled={pasteState.extracting || !pasteState.text.trim()}
                className="bg-[#5B2C82] text-white px-4 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#4A2469] disabled:opacity-40 inline-flex items-center gap-2"
              >
                <Sparkles className="w-3.5 h-3.5" /> {pasteState.extracting ? "Reading…" : "Extract with Gemini"}
              </button>
            </div>

            {pasteState.rows.length > 0 && (
              <div className="border border-[#1F1E1B] max-h-96 overflow-y-auto" data-testid="paste-extracted-rows">
                <table className="w-full text-sm">
                  <thead className="bg-[#F5F4F0] border-b-2 border-[#1F1E1B] sticky top-0">
                    <tr>
                      <th className="px-2 py-2 text-[10px] uppercase tracking-wider text-[#66645E]">✓</th>
                      <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wider text-[#66645E]">Commodity</th>
                      <th className="num px-2 py-2 text-[10px] uppercase tracking-wider text-[#66645E]">Price</th>
                      <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wider text-[#66645E]">Unit</th>
                      <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wider text-[#66645E]">Weight/Grade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pasteState.rows.map((r, i) => (
                      <tr key={i} className="border-b border-[#E5E2D6] last:border-b-0" data-testid={`extracted-row-${i}`}>
                        <td className="px-2 py-1 text-center">
                          <input type="checkbox" data-testid={`extracted-check-${i}`} checked={!!pasteState.selected[i]} onChange={() => toggleRow(i)} />
                        </td>
                        <td className="px-2 py-1">
                          <input value={r.commodity} onChange={(e) => editRow(i, { commodity: e.target.value })} className="w-full bg-transparent border-b border-transparent hover:border-[#D2CEC1] focus:border-[#1F1E1B] px-1 py-0.5 text-xs" />
                        </td>
                        <td className="num px-2 py-1">
                          <input type="number" step="0.0001" value={r.price} onChange={(e) => editRow(i, { price: parseFloat(e.target.value) || 0 })} className="w-20 bg-transparent border-b border-transparent hover:border-[#D2CEC1] focus:border-[#1F1E1B] px-1 py-0.5 text-xs text-right tabular font-bold" />
                        </td>
                        <td className="px-2 py-1">
                          <select value={r.unit} onChange={(e) => editRow(i, { unit: e.target.value })} className="text-xs bg-transparent border-b border-transparent hover:border-[#D2CEC1]">
                            {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                          </select>
                        </td>
                        <td className="px-2 py-1 text-[11px] text-[#66645E]">
                          <input value={r.weight_class || ""} onChange={(e) => editRow(i, { weight_class: e.target.value })} placeholder="wt" className="w-16 bg-transparent border-b border-transparent hover:border-[#D2CEC1] focus:border-[#1F1E1B] px-1 py-0.5 text-xs" />
                          <input value={r.grade || ""} onChange={(e) => editRow(i, { grade: e.target.value })} placeholder="grade" className="w-20 bg-transparent border-b border-transparent hover:border-[#D2CEC1] focus:border-[#1F1E1B] px-1 py-0.5 text-xs ml-1" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {pasteState.rows.length > 0 && (
              <div className="flex items-center justify-between pt-1">
                <div className="text-[11px] text-[#66645E]">
                  {Object.values(pasteState.selected).filter(Boolean).length} of {pasteState.rows.length} selected
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setPasteOpen(false)} className="border border-[#1F1E1B] px-4 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
                  <button
                    type="button"
                    data-testid="paste-save-btn"
                    onClick={saveSelected}
                    className="bg-[#1A4135] text-white px-4 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128] inline-flex items-center gap-2"
                  >
                    <Check className="w-3.5 h-3.5" /> Save Selected
                  </button>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Recent quotes for the selected commodity */}
      {compare && rows.length > 0 && (
        <div className="mt-8" data-testid="market-recent-quotes">
          <h3 className="font-display text-xl font-black uppercase tracking-tight mb-3">All {commodity} quotes (recent)</h3>
          <div className="bg-white border border-[#1F1E1B] overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F4F0] border-b-2 border-[#1F1E1B]">
                <tr>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#66645E]">Date</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#66645E]">Location</th>
                  <th className="num px-3 py-2 text-[10px] uppercase tracking-wider text-[#66645E]">Price</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#66645E]">Class</th>
                  <th className="no-print"></th>
                </tr>
              </thead>
              <tbody>
                {prices.filter((p) => p.commodity === commodity).slice(0, 30).map((p) => (
                  <tr key={p.id} className="border-b border-[#E5E2D6] last:border-b-0">
                    <td className="px-3 py-2 tabular">{p.date}</td>
                    <td className="px-3 py-2">{p.location_name}</td>
                    <td className="num px-3 py-2 tabular font-bold">{fmtUSD(p.price)} <span className="text-[10px] text-[#66645E] font-normal">{p.unit}</span></td>
                    <td className="px-3 py-2 text-[#66645E] text-xs">{[p.weight_class, p.grade].filter(Boolean).join(" · ") || "—"}</td>
                    <td className="no-print px-3 py-2 text-right">
                      <button data-testid={`delete-price-${p.id}`} onClick={() => deletePrice(p.id)} className="text-[#BC4727] hover:underline text-[10px] font-bold uppercase tracking-wider">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
